import React, { useMemo, useState, useEffect } from "react";
import ProcessRecordModal      from "../preview-modal/ProcessRecordModal";
import PortalCredentialsModal  from "../preview-modal/PortalCredentialsModal";
import {
    Alert,
    Box,
    Button,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Typography
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import { axios_tenant } from "../../../../../axios";

const BASE_COLUMNS = [
    { id: "rowNumber", label: "Row Number", numeric: true },
    { id: "fieldName", label: "Field Name", numeric: false },
    { id: "fieldValue", label: "Field Value", numeric: false },
    { id: "description", label: "Error Description", numeric: false }
];

function compareValues(a, b, key) {
    const aVal = a?.[key];
    const bVal = b?.[key];

    if (key === "rowNumber") {
        return (Number(aVal) || 0) - (Number(bVal) || 0);
    }

    return String(aVal ?? "").localeCompare(String(bVal ?? ""), undefined, {
        sensitivity: "base"
    });
}

const ValidationStep = ({ onNext, onBack, validationResult, file, setFile, setMatterProcessLoading, setResult, activateJob, joinJob, configValues, onProcessReset, setProcessError, autoOpenCredentials, onCredentialsOpened }) => {
    const [orderBy, setOrderBy] = useState("rowNumber");
    const [order, setOrder] = useState("asc");
    const [openModal, setOpenModal] = useState(false);
    const [openCredentials, setOpenCredentials] = useState(false);
    const [credentialsError, setCredentialsError] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (autoOpenCredentials) {
            setCredentialsError(null);
            setOpenCredentials(true);
            onCredentialsOpened?.();
        }
    }, [autoOpenCredentials, onCredentialsOpened]);


    const isUpdate = configValues?.action === "update";
    const idLabel = isUpdate ? "FIP Id" : "File No.";

    const columns = [
        BASE_COLUMNS[0],
        { id: "fileNo", label: idLabel, numeric: false },
        ...BASE_COLUMNS.slice(1)
    ];

    const totalRecords = validationResult?.totalRecords ?? 0;
    const validRecords = validationResult?.validRecords ?? 0;
    const errorCount = validationResult?.errorCount ?? 0;
    const errors = validationResult?.errors ?? [];
    const errorRowCount = new Set(errors.map(e => e.rowNumber)).size;

    const downloadErrorsAsCsv = () => {
        const header = ["Row Number", idLabel, "Field Name", "Field Value", "Description"];
        const rows = errors.map((e) => [
            e?.rowNumber ?? "",
            e?.fileNo ?? "",
            e?.fieldName ?? "",
            e?.fieldValue ?? "",
            e?.description ?? ""
        ]);

        const escapeCell = (value) => `"${String(value).replace(/\"/g, "\"\"")}"`;
        const csv = [header, ...rows].map((r) => r.map(escapeCell).join(",")).join("\n");

        const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `error_report_${Date.now()}`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
    };

    const handleDownloadErrorReport = () => {
        downloadErrorsAsCsv();
    };

    const sortedErrors = useMemo(() => {
        const next = [...errors];
        next.sort((a, b) => {
            const cmp = compareValues(a, b, orderBy);
            return order === "asc" ? cmp : -cmp;
        });
        return next;
    }, [errors, order, orderBy]);

    const handleSort = (columnId) => {
        if (orderBy === columnId) {
            setOrder((prev) => (prev === "asc" ? "desc" : "asc"));
            return;
        }

        setOrderBy(columnId);
        setOrder("asc");
    };

    const handleValidRecords = () => {
        setOpenModal(true);
    };

    // Called by ProcessRecordModal "Proceed" — close confirmation, open credentials dialog.
    const handleOpenCredentials = () => {
        setOpenModal(false);
        setCredentialsError(null);
        setOpenCredentials(true);
    };

    const processValidRecords = async (portalUsername, portalPassword) => {
        if (!file) return;
        setSubmitting(true);
        setCredentialsError(null);

        try {
            onProcessReset();
            setMatterProcessLoading(true);

            const jobRes = await axios_tenant.post("/adim/matter-action/start", {
                portalUsername,
                portalPassword,
            });

            const { jobId, jobToken } = jobRes.data;

            setOpenCredentials(false);
            joinJob(jobId, jobToken);
            activateJob(jobId, jobToken);
            onNext();

            const formData = new FormData();
            formData.append("file", file);
            formData.append("config", JSON.stringify(configValues));
            formData.append("jobId", jobId);
            formData.append("jobToken", jobToken);

            await axios_tenant.post(
                "/adim/matter-action",
                formData,
                { headers: { "Content-Type": "multipart/form-data" } }
            );
        } catch (err) {
            const status = err.response?.status;
            const msg    = err.response?.data?.message;
            if (status === 400 || status === 401) {
                setCredentialsError(msg || "Invalid credentials. Please check and try again.");
                setMatterProcessLoading(false);
            } else {
                setOpenCredentials(false);
                setProcessError("Failed to submit the file for processing. Please try again.");
                setMatterProcessLoading(false);
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Box>
            <Paper sx={{ p: 4, background: "#fff" }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                    <Typography variant="h5">Validation Result</Typography>

                    <Button
                        variant="text"
                        size="small"
                        startIcon={<DownloadIcon />}
                        onClick={handleDownloadErrorReport}
                        disabled={errorCount === 0}
                        sx={{ textTransform: "none" }}
                    >
                        Download Error Report
                    </Button>
                </Box>

                {errorCount > 0 ? (
                    <Alert variant="outlined" severity="error" sx={{ mb: 2 }}>
                        {errorRowCount} {errorRowCount === 1 ? "record contains" : "records contain"} errors ({errorCount} total)
                    </Alert>
                ) : (
                    <Alert variant="outlined" severity="success" sx={{ mb: 2 }}>
                        All records are valid
                    </Alert>
                )}

                <TableContainer
                    sx={{
                        border: "1px solid #e0e0e0",
                        borderRadius: 1,
                        overflow: "hidden",
                        mb: 3
                    }}
                >
                    <Table size="small" aria-label="validation-summary">
                        <TableHead>
                            <TableRow sx={{ background: "#fafafa" }}>
                                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Count</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            <TableRow>
                                <TableCell>Total Records</TableCell>
                                <TableCell>{totalRecords}</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell>Valid Records</TableCell>
                                <TableCell>{validRecords}</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell>Errors</TableCell>
                                <TableCell>{errorCount}</TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>

                <TableContainer
                    sx={{
                        border: "1px solid #e0e0e0",
                        borderRadius: 1,
                        overflow: "auto",
                        maxHeight: "calc(100vh - 420px)",
                        minHeight: 200
                    }}
                >
                    <Table stickyHeader size="small" aria-label="validation-errors">
                        <TableHead>
                            <TableRow sx={{ background: "#fafafa" }}>
                                {columns.map((col) => (
                                    <TableCell
                                        key={col.id}
                                        sortDirection={orderBy === col.id ? order : false}
                                        sx={{ fontWeight: 600 }}
                                    >
                                        <TableSortLabel
                                            active={orderBy === col.id}
                                            direction={orderBy === col.id ? order : "asc"}
                                            onClick={() => handleSort(col.id)}
                                        >
                                            {col.label}
                                        </TableSortLabel>
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {sortedErrors.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={columns.length} sx={{ py: 3, color: "text.secondary" }}>
                                        No errors to display
                                    </TableCell>
                                </TableRow>
                            ) : (
                                sortedErrors.map((err, idx) => (
                                    <TableRow key={`${err?.rowNumber ?? "row"}-${err?.fieldName ?? "field"}-${idx}`} hover>
                                        <TableCell>{err?.rowNumber ?? "-"}</TableCell>
                                        <TableCell>{err?.fileNo ?? "-"}</TableCell>
                                        <TableCell>{err?.fieldName ?? "-"}</TableCell>
                                        <TableCell sx={{ color: "error.main", fontFamily: "monospace" }}>{err?.fieldValue ?? "-"}</TableCell>
                                        <TableCell>{err?.description ?? "-"}</TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            <ProcessRecordModal
                openModal={openModal}
                setOpenModal={setOpenModal}
                validRecords={validRecords}
                processValidRecords={handleOpenCredentials}
                onNext={onNext}
            />

            <PortalCredentialsModal
                open={openCredentials}
                onClose={() => setOpenCredentials(false)}
                onSubmit={processValidRecords}
                loading={submitting}
                error={credentialsError}
            />

            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 3 }}>
                <Button
                    variant="contained"
                    disabled={validRecords === 0}
                    onClick={()=> handleValidRecords()}
                    sx={{ textTransform: "none" }}
                >
                    Proceed with Valid Records
                </Button>
                {errorCount > 0 && (
                    <Button
                        variant="outlined"
                        onClick={() => { setFile(null); onBack(); }}
                        sx={{ textTransform: "none" }}
                    >
                        Upload Corrected File
                    </Button>
                )}
            </Box>
        </Box>
    );
};

export default ValidationStep;
